import React, { useEffect, useState } from 'react';
import { fetchClientPerformance } from '../../services/vectorDashboardApi';

const DASH = '—';

const PERIOD_ROWS = [
  ['totalReturn', 'Gain', 'pct'],
  ['totalProfit', 'Profit', 'currency'],
  ['winRate', 'Win %', 'pct'],
];

function toDateStr(d) {
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

// Each window starts one day before the period boundary so the first point can
// serve as the return base (e.g. Today's return is measured against yesterday).
function buildPeriods() {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  const mondayOffset = (today.getDay() + 6) % 7; // Monday = start of week
  const weekBase = new Date(today);
  weekBase.setDate(today.getDate() - mondayOffset - 1);

  const monthBase = new Date(today.getFullYear(), today.getMonth(), 0);
  const yearBase = new Date(today.getFullYear() - 1, 11, 31);

  return [
    { id: 'today', title: 'Today', subtitle: 'Compared to yesterday', start: yesterday },
    { id: 'week', title: 'This Week', subtitle: 'Compared to last week', start: weekBase },
    { id: 'month', title: 'This Month', subtitle: 'Compared to last month', start: monthBase },
    { id: 'year', title: 'This Year', subtitle: 'Compared to last year', start: yearBase },
  ].map((p) => ({ ...p, range: { start: toDateStr(p.start), end: toDateStr(today) } }));
}

function formatValue(kind, value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return DASH;
  const num = Number(value);
  if (kind === 'currency') {
    const abs = Math.abs(num).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    return `${num < 0 ? '-' : '+'}$${abs}`;
  }
  return `${num.toFixed(0)}%`;
}

function tone(key, value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '';
  if (key !== 'totalReturn' && key !== 'totalProfit') return '';
  const num = Number(value);
  if (num > 0) return 'vd-positive';
  if (num < 0) return 'vd-negative';
  return '';
}

export default function AccountPeriodStats({ dataSeriesName, className = '' }) {
  const [periods] = useState(buildPeriods);
  const [metricsById, setMetricsById] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!dataSeriesName) {
      setMetricsById({});
      return undefined;
    }
    setLoading(true);
    (async () => {
      const entries = [];
      for (const p of periods) {
        try {
          const res = await fetchClientPerformance(dataSeriesName, p.range);
          entries.push([p.id, res.metrics || null]);
        } catch (err) {
          console.error('[period-stats]', p.id, err);
          entries.push([p.id, null]);
        }
      }
      if (!cancelled) setMetricsById(Object.fromEntries(entries));
    })()
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [dataSeriesName, periods]);

  return (
    <div className={`vd-period-cards${loading ? ' vd-tiles-loading' : ''}${className ? ` ${className}` : ''}`}>
      {periods.map((p) => {
        const metrics = metricsById[p.id] || null;
        return (
          <div className="vd-period-card" key={p.id}>
            <div className="vd-period-card-head">
              <span className="vd-period-card-title">{p.title}</span>
              <span className="vd-period-card-subtitle">{p.subtitle}</span>
            </div>
            <div className="vd-period-card-rows">
              {PERIOD_ROWS.map(([key, label, kind]) => {
                const value = metrics ? metrics[key] : null;
                return (
                  <div className="vd-period-card-row" key={label}>
                    <span className="vd-period-card-label">{label}</span>
                    <span className={`vd-period-card-value ${tone(key, value)}`}>
                      {formatValue(kind, value)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
